import React from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  DocumentTextIcon,
  BookOpenIcon,
  InformationCircleIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import './ControlPanel.css';
import RuleSelector from './RuleSelector';
import FunctionSelector from './FunctionSelector';

function ControlPanel({
  selectedRequirementFile,
  selectedRulesFile,
  isProcessing,
  handleSubmitToQueue,
  handleProcessFiles,
  removeSelectedFile,
  successMessage,
  onClose,
  manualRulesByDocType = {},
  globalRulesByDocType = {},
  selectedManualRules = [],
  selectedGlobalRules = [],
  onManualRuleToggle,
  onGlobalRuleToggle,
  onToggleAllManualRules,
  onToggleAllGlobalRules,
  docxFunctions = [],
  selectedFunctions = [],
  onFunctionToggle,
  onToggleAllFunctions,
  docxStates = [],
  selectedStates = [],
  onStateToggle,
  onToggleAllStates,
  docxMaturities = [],
  selectedMaturities = [],
  onMaturityToggle,
  onToggleAllMaturities,
  isLoadingDocxFilters,
  filteredRequirementCount,
  availableArchitectures = [],
  selectedArchitecture,
  onArchitectureChange,
  isLoadingRules,
  sfdScope = '',
  onSfdScopeChange,
}) {
  // Use the correct submit handler
  const submitHandler = handleSubmitToQueue || handleProcessFiles;

  const isDocx = selectedRequirementFile?.file_standard === 'DOCX';
  const hasZeroResults = isDocx && filteredRequirementCount === 0;
  const isSubmitDisabled = !selectedRequirementFile || isProcessing || hasZeroResults;
  // Legacy .doc uploads are always the narrative ELM/DOORS export — always
  // scoped to the "Functional Requirements" heading, no scope selection needed.
  const isLegacyDoc = /\.doc$/i.test(selectedRequirementFile?.name || '');
  const isSfd = selectedRequirementFile?.file_standard === 'SFD';

  return (
    <div className="h-full bg-white dark:bg-neutral-900 p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
            Processing Control Panel
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Configure and submit your analysis job
          </p>
        </div>
        {onClose && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          </motion.button>
        )}
      </div>

      <div className="space-y-4">
        {/* Requirements File */}
        <div className="card p-4">
          <label className="input-label mb-2">Requirements File (Required)</label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              {selectedRequirementFile ? (
                <div className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      {selectedRequirementFile.name}
                    </span>
                  </div>
                  {removeSelectedFile && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeSelectedFile('requirements')}
                      className="p-1 hover:bg-primary-100 dark:hover:bg-primary-800/30 rounded"
                    >
                      <XMarkIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </motion.button>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    No file selected
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rules File */}
        <div className="card p-4">
          <label className="input-label mb-2">Rules File (Optional)</label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              {selectedRulesFile ? (
                <div className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-200 dark:border-secondary-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <BookOpenIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
                    <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      {selectedRulesFile.name ? selectedRulesFile.name : null}
                    </span>
                  </div>
                  {removeSelectedFile && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeSelectedFile('rules')}
                      className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800/30 rounded"
                    >
                      <XMarkIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                    </motion.button>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    No file selected
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Architecture Selector (FUSA only) */}
        {(selectedRequirementFile?.file_standard === 'FUSA' || selectedRequirementFile?.file_standard === 'FUSA_FSC') && (
          <div className="card p-4">
            <label htmlFor="architecture" className="input-label mb-2">
              Architecture (Component Validation)
            </label>
            <select
              id="architecture"
              value={selectedArchitecture || ''}
              onChange={(e) => onArchitectureChange(e.target.value)}
              className="form-input"
            >
              <option value="">None (Skip component validation)</option>
              {availableArchitectures.map((arch) => (
                <option key={arch} value={arch}>
                  {arch}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
              Select architecture for component allocation validation
            </p>
          </div>
        )}

        {/* SFD Doc Type (table-variant .docx uploads only; legacy .doc uploads
             are always narrative-scoped, no selection needed) */}
        {isSfd && !isLegacyDoc && (
          <div className="card p-4">
            <label htmlFor="sfd-scope" className="input-label mb-2">
              SFD Doc Type
            </label>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
              Select the document type to filter requirements by the
              corresponding artifact types.
            </p>
            <select
              id="sfd-scope"
              value={sfdScope}
              onChange={(e) => onSfdScopeChange(e.target.value)}
              className="form-input"
            >
              <option value="feature_arch">Feature Arch</option>
              <option value="sub_system">Sub System</option>
            </select>
          </div>
        )}

        {/* DOCX Filters */}
        {selectedRequirementFile?.file_standard === 'DOCX' && (
          <>
            <FunctionSelector
              title="Function Name"
              functions={docxFunctions}
              selectedFunctions={selectedFunctions}
              onFunctionToggle={onFunctionToggle}
              onToggleAll={onToggleAllFunctions}
              loading={isLoadingDocxFilters}
            />
            <FunctionSelector
              title="State"
              functions={docxStates}
              selectedFunctions={selectedStates}
              onFunctionToggle={onStateToggle}
              onToggleAll={onToggleAllStates}
              loading={isLoadingDocxFilters}
            />
            <FunctionSelector
              title="Maturity"
              functions={docxMaturities}
              selectedFunctions={selectedMaturities}
              onFunctionToggle={onMaturityToggle}
              onToggleAll={onToggleAllMaturities}
              loading={isLoadingDocxFilters}
            />
          </>
        )}

        {/* Global Rules Selector */}
        {globalRulesByDocType[selectedRequirementFile?.file_standard]?.length > 0 && (
          <RuleSelector
            title="Global Rules"
            rules={globalRulesByDocType[selectedRequirementFile?.file_standard] || []}
            selectedRules={selectedGlobalRules}
            onRuleToggle={onGlobalRuleToggle}
            onToggleAll={onToggleAllGlobalRules}
            loading={isLoadingRules}
          />
        )}

        {/* Manual Rules Selector */}
        {manualRulesByDocType[selectedRequirementFile?.file_standard]?.length > 0 && (
          <RuleSelector
            title="Manual Rules"
            rules={manualRulesByDocType[selectedRequirementFile?.file_standard] || []}
            selectedRules={selectedManualRules}
            onRuleToggle={onManualRuleToggle}
            onToggleAll={onToggleAllManualRules}
            loading={isLoadingRules}
          />
        )}

        {/* Filtered Requirement Count (DOCX only) */}
        {isDocx && filteredRequirementCount !== null && (
          <div className={`card p-4 ${hasZeroResults
            ? 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
          }`}>
            <p className={`text-sm font-medium ${hasZeroResults
              ? 'text-danger-700 dark:text-danger-400'
              : 'text-primary-700 dark:text-primary-400'
            }`}>
              {hasZeroResults
                ? 'No requirements match the current filters. Adjust your selection to continue.'
                : `${filteredRequirementCount} requirement${filteredRequirementCount !== 1 ? 's' : ''} match the current filters`
              }
            </p>
          </div>
        )}

        {/* Queue Info */}
        <div className="card p-4 bg-info-50 dark:bg-info-900/20 border-info-200 dark:border-info-800">
          <div className="flex gap-3">
            <InformationCircleIcon className="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-info-700 dark:text-info-400">
              Your job will be added to the processing queue.
              You'll be able to track progress in real-time and download results when complete.
              Maximum 100 requirements per job.
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={submitHandler}
          disabled={isSubmitDisabled}
          className="w-full btn-primary flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Submitting to Queue...</span>
            </>
          ) : (
            <>
              <RocketLaunchIcon className="w-5 h-5" />
              <span>Submit to Queue</span>
            </>
          )}
        </motion.button>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
          >
            <p className="text-sm font-medium text-green-700 dark:text-green-400">
              {successMessage}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ControlPanel;